import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Receipt,
  Plus,
  Trash2,
  RefreshCw,
  BarChart3,
  Euro,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  Banknote,
} from "lucide-react";
import i18n from "../i18n";
import { apiClient } from "../services/api";
import { useAuth } from "../hooks/useAuth";

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  submitted:  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  approved:   "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected:   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  reimbursed: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};
const STATUS_ICONS = {
  draft:      Clock,
  submitted:  Send,
  approved:   CheckCircle,
  rejected:   XCircle,
  reimbursed: Banknote,
};

const StatusBadge = ({ status, label }) => {
  const Icon = STATUS_ICONS[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || ""}`}>
      <Icon size={11} />
      {label}
    </span>
  );
};

const fmt = (n, currency = "EUR") =>
  (n ?? 0).toLocaleString(i18n.language, { style: "currency", currency, minimumFractionDigits: 2 });

const categoryLabel = (category, t) => t(`expensesHub.categories.${category}`, { defaultValue: category });

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue" }) => {
  const colors = {
    blue:   "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    green:  "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
    red:    "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}><Icon size={20} /></div>
      </div>
    </div>
  );
};

// ─── Tab: Dashboard ──────────────────────────────────────────────────────────

const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/expenses/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title={t("expensesHub.dashboard.submitted")} value={data.submitted_count} sub={t("expensesHub.dashboard.submittedSub")} icon={Send} color="blue" />
        <KpiCard title={t("expensesHub.dashboard.approved")} value={data.approved_count} sub={t("expensesHub.dashboard.approvedSub")} icon={CheckCircle} color="green" />
        <KpiCard title={t("expensesHub.dashboard.pendingAmount")} value={fmt(data.pending_amount, data.currency)} icon={Euro} color="orange" />
        <KpiCard title={t("expensesHub.dashboard.reimbursedTotal")} value={fmt(data.reimbursed_amount, data.currency)} icon={Banknote} color="purple" />
      </div>

      {data.by_category?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><BarChart3 size={16} /> {t("expensesHub.dashboard.byCategory")}</h3>
          </div>
          <div className="divide-y divide-border">
            {data.by_category.map((c) => (
              <div key={c.category} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{t("expensesHub.dashboard.reportsCount", { count: c.count })}</p>
                </div>
                <span className="font-semibold text-sm">{fmt(c.total, data.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.top_spenders?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><Euro size={16} /> {t("expensesHub.dashboard.topSpenders")}</h3>
          </div>
          <div className="divide-y divide-border">
            {data.top_spenders.map((s) => (
              <div key={s.employee_id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{s.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{t("expensesHub.dashboard.expenseNotes", { count: s.reports })}</p>
                </div>
                <span className="font-semibold">{fmt(s.total, data.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Create report modal ─────────────────────────────────────────────────────

const REPORT_CATEGORIES = [
  "travel",
  "meal",
  "accommodation",
  "supplies",
  "training",
  "other",
];

const ITEM_CATEGORIES = [
  "transport",
  "meal",
  "accommodation",
  "fuel",
  "parking",
  "taxi",
  "other",
];

const CreateReportModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const isManager = hasPermission("payroll:view");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", category: "travel", currency: "EUR", destination: "", trip_start_date: today, trip_end_date: today, employee_id: "" });
  const [items, setItems] = useState([{ description: "", category: "transport", amount: "", expense_date: today }]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isManager) return;
    apiClient.get("v1/employees/?limit=200")
      .then((r) => setEmployees(r.data.employees || r.data.items || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => setEmployees([]));
  }, [isManager]);

  const addItem = () => setItems([...items, { description: "", category: "other", amount: "", expense_date: today }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await apiClient.post("v1/expenses/reports", {
        ...form,
        employee_id: isManager && form.employee_id ? parseInt(form.employee_id, 10) : undefined,
        items: items.filter((it) => it.description && it.amount).map((it) => ({ ...it, amount: parseFloat(it.amount) })),
      });
      onCreated();
      onClose();
    } catch (e) {
      console.error(e);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("expensesHub.createModal.title")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Report header */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.reportTitle")}</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("expensesHub.createModal.reportTitlePlaceholder")} />
            </div>
            {isManager && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.employee")}</label>
                <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                  <option value="">{t("expensesHub.createModal.employeePlaceholder")}</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}{emp.registration_number ? ` (${emp.registration_number})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.category")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {REPORT_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c, t)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.currency")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                <option value="TND">TND د.ت</option>
                <option value="EUR">EUR €</option>
                <option value="USD">USD $</option>
                <option value="QAR">QAR ر.ق</option>
                <option value="SAR">SAR ر.س</option>
                <option value="MAD">MAD د.م.</option>
                <option value="QAR">QAR ﷼</option>
                <option value="MAD">MAD د.م.</option>
                <option value="USD">USD $</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.startDate")}</label>
              <input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.trip_start_date} onChange={(e) => setForm({ ...form, trip_start_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.endDate")}</label>
              <input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.trip_end_date} onChange={(e) => setForm({ ...form, trip_end_date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">{t("expensesHub.createModal.destination")}</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder={t("expensesHub.createModal.destinationPlaceholder")} />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">{t("expensesHub.createModal.items")}</label>
              <button onClick={addItem} className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">
                <Plus size={12} /> {t("expensesHub.createModal.add")}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <input className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs"
                      placeholder={t("expensesHub.createModal.descriptionPlaceholder")}
                      value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <select className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs"
                      value={it.category} onChange={(e) => updateItem(i, "category", e.target.value)}>
                      {ITEM_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c, t)}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs"
                      placeholder={t("expensesHub.createModal.amountPlaceholder")}
                      value={it.amount} onChange={(e) => updateItem(i, "amount", e.target.value)} />
                  </div>
                  <div className="col-span-3">
                    <input type="date" className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs"
                      value={it.expense_date} onChange={(e) => updateItem(i, "expense_date", e.target.value)} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={() => removeItem(i)} className="p-1 text-muted-foreground hover:text-red-500">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right text-sm font-semibold mt-2">
              {t("expensesHub.common.total")} {fmt(total, form.currency)}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("expensesHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.title.trim()}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("expensesHub.common.creating") : t("expensesHub.common.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Report card ─────────────────────────────────────────────────────────────

const RejectModal = ({ reportId, onClose, onRejected }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    try {
      await apiClient.post(`v1/expenses/reports/${reportId}/reject`, { reason });
      onRejected();
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">{t("expensesHub.rejectModal.title")}</h2>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5">
          <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" rows={3}
            placeholder={t("expensesHub.rejectModal.placeholder")} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("expensesHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !reason.trim()} className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white disabled:opacity-50">
            {saving ? "..." : t("expensesHub.rejectModal.reject")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportCard = ({ report, isApprover, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const action = async (endpoint) => {
    await apiClient.post(`v1/expenses/reports/${report.id}/${endpoint}`);
    onRefresh();
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold">{report.title}</p>
            <StatusBadge status={report.status} label={report.status_label} />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {report.employee_name} · {report.category_label}
            {report.destination && ` · ${report.destination}`}
            {report.submitted_at && ` · ${t("expensesHub.reportCard.submittedOn", { date: new Date(report.submitted_at).toLocaleDateString(i18n.language) })}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-bold text-sm">{fmt(report.total_amount, report.currency)}</span>
          {expanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {/* Items table */}
          {report.items?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium text-muted-foreground">{t("expensesHub.reportCard.headers.description")}</th>
                    <th className="text-left px-5 py-2 font-medium text-muted-foreground">{t("expensesHub.reportCard.headers.category")}</th>
                    <th className="text-right px-5 py-2 font-medium text-muted-foreground">{t("expensesHub.reportCard.headers.amount")}</th>
                    <th className="text-left px-5 py-2 font-medium text-muted-foreground">{t("expensesHub.reportCard.headers.date")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {report.items.map((it) => (
                    <tr key={it.id}>
                      <td className="px-5 py-2">{it.description}</td>
                      <td className="px-5 py-2 text-muted-foreground">{it.category_label}</td>
                      <td className="px-5 py-2 text-right font-medium">{fmt(it.amount, it.currency)}</td>
                      <td className="px-5 py-2 text-muted-foreground">{it.expense_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.rejection_reason && (
            <div className="px-5 py-3 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 text-sm">
              <strong>{t("expensesHub.reportCard.rejectionReason")}</strong> {report.rejection_reason}
            </div>
          )}

          {/* Actions */}
          <div className="px-5 py-3 border-t border-border flex gap-2 flex-wrap">
            {report.status === "draft" && (
              <button onClick={() => action("submit")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-blue-600 text-white hover:bg-blue-700">
                <Send size={12} /> {t("expensesHub.reportCard.submit")}
              </button>
            )}
            {isApprover && report.status === "submitted" && (
              <>
                <button onClick={() => action("approve")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-green-600 text-white hover:bg-green-700">
                  <CheckCircle size={12} /> {t("expensesHub.reportCard.approve")}
                </button>
                <button onClick={() => setShowReject(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-red-600 text-white hover:bg-red-700">
                  <XCircle size={12} /> {t("expensesHub.reportCard.reject")}
                </button>
              </>
            )}
            {isApprover && report.status === "approved" && (
              <button onClick={() => action("reimburse")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-purple-600 text-white hover:bg-purple-700">
                <Banknote size={12} /> {t("expensesHub.reportCard.reimburse")}
              </button>
            )}
          </div>
        </div>
      )}

      {showReject && (
        <RejectModal reportId={report.id} onClose={() => setShowReject(false)} onRejected={onRefresh} />
      )}
    </div>
  );
};

// ─── Tab: Mes notes de frais ──────────────────────────────────────────────────

const MyReportsTab = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = statusFilter !== "all" ? `&status=${statusFilter}` : "";
    apiClient.get(`v1/expenses/reports?limit=100${q}`).then((r) => setReports(r.data.items || [])).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = [
    { key: "all", label: t("expensesHub.filters.all") },
    { key: "draft", label: t("expensesHub.filters.draft") },
    { key: "submitted", label: t("expensesHub.filters.submitted") },
    { key: "approved", label: t("expensesHub.filters.approved") },
    { key: "reimbursed", label: t("expensesHub.filters.reimbursed") },
    { key: "rejected", label: t("expensesHub.filters.rejected") },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={load} title={t("expensesHub.common.refresh")} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("expensesHub.myReports.new")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Receipt size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">{t("expensesHub.myReports.emptyTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("expensesHub.myReports.emptySubtitle")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} isApprover={false} onRefresh={load} />
          ))}
        </div>
      )}

      {showCreate && <CreateReportModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
};

// ─── Tab: Approbation ─────────────────────────────────────────────────────────

const ApprovalTab = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("submitted");

  const load = useCallback(() => {
    setLoading(true);
    const q = statusFilter !== "all" ? `&status=${statusFilter}` : "";
    apiClient.get(`v1/expenses/reports?limit=100${q}`).then((r) => setReports(r.data.items || [])).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = [
    { key: "submitted", label: t("expensesHub.filters.toApprove") },
    { key: "approved", label: t("expensesHub.filters.approved") },
    { key: "rejected", label: t("expensesHub.filters.rejected") },
    { key: "reimbursed", label: t("expensesHub.filters.reimbursed") },
    { key: "all", label: t("expensesHub.filters.all") },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} title={t("expensesHub.common.refresh")} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CheckCircle size={36} className="mx-auto mb-2 opacity-50" />
          {t("expensesHub.approval.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} isApprover={true} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Hub ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "my-reports", icon: Receipt },
  { id: "approval", icon: CheckCircle },
];

export default function ExpensesHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "my-reports": return <MyReportsTab />;
      case "approval":   return <ApprovalTab />;
      default:           return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt size={24} className="text-primary" />
            {t("expensesHub.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("expensesHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} />
            {t(`expensesHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
}
